import React, { useState } from 'react';
import { UploadIcon, FileTextIcon, CheckCircleIcon, AlertCircleIcon, DownloadIcon, PlusIcon, XIcon, TagIcon } from 'lucide-react';
interface Category {
  id: string;
  name: string;
  type: 'ingresos' | 'costos' | 'gastos' | 'otros' | 'activos' | 'pasivos' | 'capital' | 'operacion' | 'inversion' | 'financiamiento';
}
const DataImport = () => {
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedFileType, setSelectedFileType] = useState('');
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [activeTab, setActiveTab] = useState<'ingresos' | 'costos' | 'gastos' | 'otros' | 'activos' | 'pasivos' | 'capital' | 'operacion' | 'inversion' | 'financiamiento'>('ingresos');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [activeStatement, setActiveStatement] = useState<'income' | 'balance' | 'cashflow'>('income');
  // Categories state
  const [categories, setCategories] = useState<Category[]>([
  // Estado de Resultados
  {
    id: '1',
    name: 'Ventas de productos',
    type: 'ingresos'
  }, {
    id: '2',
    name: 'Servicios',
    type: 'ingresos'
  }, {
    id: '3',
    name: 'Materia prima',
    type: 'costos'
  }, {
    id: '4',
    name: 'Mano de obra',
    type: 'costos'
  }, {
    id: '5',
    name: 'Nómina',
    type: 'gastos'
  }, {
    id: '6',
    name: 'Renta',
    type: 'gastos'
  }, {
    id: '7',
    name: 'Marketing',
    type: 'gastos'
  }, {
    id: '8',
    name: 'Depreciación',
    type: 'otros'
  }, {
    id: '9',
    name: 'Impuestos',
    type: 'otros'
  },
  // Balance General
  {
    id: '10',
    name: 'Efectivo y bancos',
    type: 'activos'
  }, {
    id: '11',
    name: 'Cuentas por cobrar',
    type: 'activos'
  }, {
    id: '12',
    name: 'Inventario',
    type: 'activos'
  }, {
    id: '13',
    name: 'Activos fijos',
    type: 'activos'
  }, {
    id: '14',
    name: 'Cuentas por pagar',
    type: 'pasivos'
  }, {
    id: '15',
    name: 'Deuda a corto plazo',
    type: 'pasivos'
  }, {
    id: '16',
    name: 'Deuda a largo plazo',
    type: 'pasivos'
  }, {
    id: '17',
    name: 'Capital social',
    type: 'capital'
  }, {
    id: '18',
    name: 'Utilidades retenidas',
    type: 'capital'
  },
  // Flujo de Efectivo
  {
    id: '19',
    name: 'Cobros a clientes',
    type: 'operacion'
  }, {
    id: '20',
    name: 'Pagos a proveedores',
    type: 'operacion'
  }, {
    id: '21',
    name: 'Pago de nómina',
    type: 'operacion'
  }, {
    id: '22',
    name: 'Venta de activos',
    type: 'inversion'
  }, {
    id: '23',
    name: 'Compra de activos',
    type: 'inversion'
  }, {
    id: '24',
    name: 'Préstamos recibidos',
    type: 'financiamiento'
  }, {
    id: '25',
    name: 'Pago de préstamos',
    type: 'financiamiento'
  }]);
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && selectedFileType) {
      setTimeout(() => {
        setUploadStatus('success');
        setTimeout(() => setUploadStatus('idle'), 3000);
      }, 1500);
    }
  };
  const handleAddCategory = () => {
    if (newCategoryName.trim()) {
      const newCategory: Category = {
        id: Date.now().toString(),
        name: newCategoryName.trim(),
        type: activeTab
      };
      setCategories([...categories, newCategory]);
      setNewCategoryName('');
    }
  };
  const handleDeleteCategory = (id: string) => {
    setCategories(categories.filter(cat => cat.id !== id));
  };
  const getCategoriesByType = (type: Category['type']) => {
    return categories.filter(cat => cat.type === type);
  };
  const categoryTabs = {
    income: [{
      id: 'ingresos' as const,
      label: 'Ingresos',
      color: 'blue'
    }, {
      id: 'costos' as const,
      label: 'Costos de Venta',
      color: 'orange'
    }, {
      id: 'gastos' as const,
      label: 'Gastos Operativos',
      color: 'purple'
    }, {
      id: 'otros' as const,
      label: 'Otros Gastos',
      color: 'gray'
    }],
    balance: [{
      id: 'activos' as const,
      label: 'Activos',
      color: 'blue'
    }, {
      id: 'pasivos' as const,
      label: 'Pasivos',
      color: 'orange'
    }, {
      id: 'capital' as const,
      label: 'Capital',
      color: 'green'
    }],
    cashflow: [{
      id: 'operacion' as const,
      label: 'Actividades de Operación',
      color: 'blue'
    }, {
      id: 'inversion' as const,
      label: 'Actividades de Inversión',
      color: 'purple'
    }, {
      id: 'financiamiento' as const,
      label: 'Actividades de Financiamiento',
      color: 'green'
    }]
  };
  const getTabColor = (tabId: string) => {
    const allTabs = [...categoryTabs.income, ...categoryTabs.balance, ...categoryTabs.cashflow];
    const tab = allTabs.find(t => t.id === tabId);
    return tab?.color || 'blue';
  };
  const getCurrentTabs = () => {
    return categoryTabs[activeStatement];
  };
  const financialCategories = {
    income: [{
      name: 'Ingresos',
      type: 'ingresos' as const,
      color: 'blue'
    }, {
      name: 'Costos de Venta',
      type: 'costos' as const,
      color: 'orange'
    }, {
      name: 'Gastos Operativos',
      type: 'gastos' as const,
      color: 'purple'
    }, {
      name: 'Otros Gastos',
      type: 'otros' as const,
      color: 'gray'
    }],
    balance: [{
      name: 'Activos',
      type: 'activos' as const,
      color: 'blue'
    }, {
      name: 'Pasivos',
      type: 'pasivos' as const,
      color: 'orange'
    }, {
      name: 'Capital',
      type: 'capital' as const,
      color: 'green'
    }],
    cashflow: [{
      name: 'Actividades de Operación',
      type: 'operacion' as const,
      color: 'blue'
    }, {
      name: 'Actividades de Inversión',
      type: 'inversion' as const,
      color: 'purple'
    }, {
      name: 'Actividades de Financiamiento',
      type: 'financiamiento' as const,
      color: 'green'
    }]
  };
  return <div data-id="element-517">
      <div className="mb-6" data-id="element-518">
        <h1 className="text-2xl font-bold text-gray-800" data-id="element-519">
          Importación de Datos
        </h1>
        <p className="text-gray-600" data-id="element-520">
          Carga datos financieros para las empresas de la plataforma
        </p>
      </div>
      {/* Company and Period Selection */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm mb-6" data-id="element-521">
        <h2 className="text-lg font-semibold mb-4" data-id="element-522">
          Seleccionar empresa y período
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-523">
          <div data-id="element-524">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-525">
              Empresa
            </label>
            <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-526">
              <option value="" data-id="element-527">Seleccionar empresa</option>
              <option value="1" data-id="element-528">PYME Solutions</option>
              <option value="2" data-id="element-529">Retail Express</option>
              <option value="3" data-id="element-530">Manufacturing Pro</option>
              <option value="4" data-id="element-531">Services Plus</option>
            </select>
          </div>
          <div data-id="element-532">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-533">
              Período
            </label>
            <input type="month" value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-534" />
          </div>
        </div>
      </div>
      {/* Category Management Section */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm mb-6" data-id="element-535">
        <div className="flex items-center space-x-2 mb-6" data-id="element-536">
          <TagIcon size={20} className="text-gray-600" data-id="element-537" />
          <h2 className="text-lg font-semibold" data-id="element-538">Gestión de Categorías</h2>
        </div>
        <p className="text-sm text-gray-600 mb-6" data-id="element-539">
          Crea y administra las categorías para organizar los datos financieros
          de esta empresa
        </p>
        {/* Statement Type Selector */}
        <div className="flex space-x-2 mb-6 p-1 bg-gray-100 rounded-lg w-fit" data-id="element-540">
          <button onClick={() => {
          setActiveStatement('income');
          setActiveTab('ingresos');
        }} className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${activeStatement === 'income' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-541">
            Estado de Resultados
          </button>
          <button onClick={() => {
          setActiveStatement('balance');
          setActiveTab('activos');
        }} className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${activeStatement === 'balance' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-542">
            Balance General
          </button>
          <button onClick={() => {
          setActiveStatement('cashflow');
          setActiveTab('operacion');
        }} className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${activeStatement === 'cashflow' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-543">
            Flujo de Efectivo
          </button>
        </div>
        {/* Tabs */}
        <div className="flex space-x-2 mb-6 border-b border-gray-200 overflow-x-auto" data-id="element-544">
          {getCurrentTabs().map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2 font-medium text-sm transition-colors relative whitespace-nowrap ${activeTab === tab.id ? `text-${tab.color}-600 border-b-2 border-${tab.color}-600` : 'text-gray-500 hover:text-gray-700'}`} data-id="element-545">
              {tab.label}
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${activeTab === tab.id ? `bg-${tab.color}-100 text-${tab.color}-700` : 'bg-gray-100 text-gray-600'}`} data-id="element-546">
                {getCategoriesByType(tab.id).length}
              </span>
            </button>)}
        </div>
        {/* Add Category Input */}
        <div className="mb-6" data-id="element-547">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-548">
            Nueva categoría de{' '}
            {getCurrentTabs().find(t => t.id === activeTab)?.label}
          </label>
          <div className="flex space-x-2" data-id="element-549">
            <input type="text" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} onKeyPress={e => {
            if (e.key === 'Enter') {
              handleAddCategory();
            }
          }} placeholder="Ej: Ventas online, Efectivo en caja, etc." className="flex-1 p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-550" />
            <button onClick={handleAddCategory} disabled={!newCategoryName.trim()} className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2" data-id="element-551">
              <PlusIcon size={18} data-id="element-552" />
              <span data-id="element-553">Agregar</span>
            </button>
          </div>
        </div>
        {/* Categories List */}
        <div data-id="element-554">
          <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-555">
            Categorías de{' '}
            {getCurrentTabs().find(t => t.id === activeTab)?.label}
          </h3>
          <div className="space-y-2" data-id="element-556">
            {getCategoriesByType(activeTab).length === 0 ? <div className="text-center py-8 text-gray-500" data-id="element-557">
                <TagIcon size={48} className="mx-auto mb-2 text-gray-300" data-id="element-558" />
                <p data-id="element-559">No hay categorías creadas</p>
                <p className="text-sm" data-id="element-560">Agrega tu primera categoría arriba</p>
              </div> : getCategoriesByType(activeTab).map(category => <div key={category.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors" data-id="element-561">
                  <div className="flex items-center space-x-3" data-id="element-562">
                    <div className={`w-2 h-2 rounded-full bg-${getTabColor(activeTab)}-600`} data-id="element-563"></div>
                    <span className="text-gray-900" data-id="element-564">{category.name}</span>
                  </div>
                  <button onClick={() => handleDeleteCategory(category.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" data-id="element-565">
                    <XIcon size={16} data-id="element-566" />
                  </button>
                </div>)}
          </div>
        </div>
      </div>
      {/* Manual Input Section */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm mb-6" data-id="element-567">
        <h2 className="text-lg font-semibold mb-4" data-id="element-568">Entrada manual de datos</h2>
        <p className="text-sm text-gray-600 mb-6" data-id="element-569">
          Ingresa los valores para cada categoría creada
        </p>
        {/* Statement Type Tabs for Manual Input */}
        <div className="flex space-x-2 mb-6 border-b border-gray-200" data-id="element-570">
          <button onClick={() => setActiveStatement('income')} className={`px-4 py-2 font-medium text-sm transition-colors ${activeStatement === 'income' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-571">
            Estado de Resultados
          </button>
          <button onClick={() => setActiveStatement('balance')} className={`px-4 py-2 font-medium text-sm transition-colors ${activeStatement === 'balance' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-572">
            Balance General
          </button>
          <button onClick={() => setActiveStatement('cashflow')} className={`px-4 py-2 font-medium text-sm transition-colors ${activeStatement === 'cashflow' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-573">
            Flujo de Efectivo
          </button>
        </div>
        <div className="space-y-6" data-id="element-574">
          {financialCategories[activeStatement].map((category, index) => {
          const categoryItems = getCategoriesByType(category.type);
          if (categoryItems.length === 0) return null;
          return <div key={index} data-id="element-575">
                <div className="flex items-center space-x-2 mb-3" data-id="element-576">
                  <div className={`w-3 h-3 rounded-full bg-${category.color}-600`} data-id="element-577"></div>
                  <h3 className="font-medium text-gray-900" data-id="element-578">{category.name}</h3>
                  <span className="text-sm text-gray-500" data-id="element-579">
                    ({categoryItems.length} categorías)
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-580">
                  {categoryItems.map(item => <div key={item.id} data-id="element-581">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-582">
                        {item.name}
                      </label>
                      <input type="number" placeholder="0.00" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" disabled={!selectedCompany || !selectedPeriod} data-id="element-583" />
                    </div>)}
                </div>
              </div>;
        })}
          {categories.length === 0 && <div className="text-center py-12 text-gray-500" data-id="element-584">
              <TagIcon size={64} className="mx-auto mb-4 text-gray-300" data-id="element-585" />
              <p className="text-lg font-medium mb-2" data-id="element-586">
                No hay categorías creadas
              </p>
              <p className="text-sm" data-id="element-587">
                Crea categorías en la sección "Gestión de Categorías" para
                empezar a ingresar datos
              </p>
            </div>}
        </div>
        {categories.length > 0 && <div className="flex justify-end mt-6" data-id="element-588">
            <button disabled={!selectedCompany || !selectedPeriod} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-589">
              Guardar datos
            </button>
          </div>}
      </div>
      {/* Upload Section */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm mb-6" data-id="element-590">
        <h2 className="text-lg font-semibold mb-4" data-id="element-591">Cargar archivo</h2>
        {/* File Type Selection */}
        <div className="mb-6" data-id="element-592">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-593">
            Tipo de estado financiero
          </label>
          <select value={selectedFileType} onChange={e => setSelectedFileType(e.target.value)} disabled={!selectedCompany || !selectedPeriod} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-594">
            <option value="" data-id="element-595">Seleccionar tipo de archivo</option>
            <option value="income" data-id="element-596">Estado de Resultados</option>
            <option value="cashflow" data-id="element-597">Flujo de Efectivo</option>
            <option value="balance" data-id="element-598">Balance General</option>
          </select>
          {!selectedCompany || !selectedPeriod ? <p className="text-sm text-gray-500 mt-2" data-id="element-599">
              Primero selecciona una empresa y período
            </p> : null}
        </div>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors" data-id="element-600">
          <input type="file" id="file-upload" className="hidden" accept=".csv,.xlsx,.xls" onChange={handleFileUpload} disabled={!selectedCompany || !selectedPeriod || !selectedFileType} data-id="element-601" />
          <label htmlFor="file-upload" className={`cursor-pointer ${!selectedCompany || !selectedPeriod || !selectedFileType ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-602">
            <UploadIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-603" />
            <p className="text-lg font-medium text-gray-700 mb-2" data-id="element-604">
              Arrastra tu archivo aquí o haz clic para seleccionar
            </p>
            <p className="text-sm text-gray-500" data-id="element-605">
              Formatos soportados: CSV, XLSX, XLS
            </p>
            {selectedFileType && <p className="text-sm text-blue-600 mt-2 font-medium" data-id="element-606">
                Tipo seleccionado:{' '}
                {selectedFileType === 'income' ? 'Estado de Resultados' : selectedFileType === 'cashflow' ? 'Flujo de Efectivo' : 'Balance General'}
              </p>}
          </label>
        </div>
        {uploadStatus === 'success' && <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3" data-id="element-607">
            <CheckCircleIcon size={24} className="text-green-600" data-id="element-608" />
            <div data-id="element-609">
              <p className="font-medium text-green-900" data-id="element-610">
                Archivo cargado exitosamente
              </p>
              <p className="text-sm text-green-700" data-id="element-611">
                Los datos han sido procesados y están disponibles
              </p>
            </div>
          </div>}
        {uploadStatus === 'error' && <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3" data-id="element-612">
            <AlertCircleIcon size={24} className="text-red-600" data-id="element-613" />
            <div data-id="element-614">
              <p className="font-medium text-red-900" data-id="element-615">
                Error al cargar archivo
              </p>
              <p className="text-sm text-red-700" data-id="element-616">
                Por favor verifica el formato del archivo
              </p>
            </div>
          </div>}
      </div>
      {/* Template Download */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-617">
        <h2 className="text-lg font-semibold mb-4" data-id="element-618">
          Plantillas de importación
        </h2>
        <p className="text-sm text-gray-600 mb-4" data-id="element-619">
          Descarga nuestras plantillas para facilitar la importación de datos
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-620">
          <button className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors" data-id="element-621">
            <div className="flex items-center space-x-3" data-id="element-622">
              <FileTextIcon size={24} className="text-blue-600" data-id="element-623" />
              <span className="font-medium" data-id="element-624">Estado de Resultados</span>
            </div>
            <DownloadIcon size={20} className="text-gray-400" data-id="element-625" />
          </button>
          <button className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors" data-id="element-626">
            <div className="flex items-center space-x-3" data-id="element-627">
              <FileTextIcon size={24} className="text-blue-600" data-id="element-628" />
              <span className="font-medium" data-id="element-629">Flujo de Efectivo</span>
            </div>
            <DownloadIcon size={20} className="text-gray-400" data-id="element-630" />
          </button>
          <button className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors" data-id="element-631">
            <div className="flex items-center space-x-3" data-id="element-632">
              <FileTextIcon size={24} className="text-blue-600" data-id="element-633" />
              <span className="font-medium" data-id="element-634">Balance General</span>
            </div>
            <DownloadIcon size={20} className="text-gray-400" data-id="element-635" />
          </button>
        </div>
      </div>
    </div>;
};
export default DataImport;